import { defineStore } from "pinia";
import { ProfileAdministrationAPI } from "../api";
import { PagedProjects, Project, ProjectSurface } from "../types/PersologOnlineTypes.ts";

export type ProjectSortMode = "creation" | "edited";
export type ProjectSortDirection = "asc" | "desc";

/**
 * Central store for the project overview. It owns the paged "surface" data (the lightweight project
 * list driving the overview) together with all filter, sort and paging state, so the overview only
 * ever holds a single page of projects in memory instead of loading every project — and every
 * participant — up front. Core project data is loaded on demand when a project is opened and cached
 * in {@link detailCache}.
 */
export const useProjectsStore = defineStore("projects", {
    state: () => ({
        trainerId: "",
        /** The current page of project surfaces returned by the backend. */
        items: [] as ProjectSurface[],
        /** Total number of projects matching the current filters (across all pages). */
        totalRecords: 0,
        page: 0,
        pageSize: 20,
        sort: "edited" as ProjectSortMode,
        direction: "desc" as ProjectSortDirection,
        search: "",
        products: [] as string[],
        from: undefined as string | undefined,
        to: undefined as string | undefined,
        archived: false,
        hasArchivedProjects: false,
        loading: false,
        /** Full project data keyed by project id, populated when a project is opened. */
        detailCache: {} as Record<number, Project>,
    }),
    getters: {
        /** Whether any filter (search, product, date range) is currently narrowing the result set. */
        hasActiveFilters: (state): boolean =>
            state.search.trim().length > 0 ||
            state.products.length > 0 ||
            state.from !== undefined ||
            state.to !== undefined,
    },
    actions: {
        /** Loads the given page (defaults to the current page) with the active filters and sort. */
        async loadPage(page?: number) {
            if (!this.trainerId) return;
            const targetPage = page ?? this.page;
            this.loading = true;
            try {
                const response: PagedProjects = await ProfileAdministrationAPI.getProjectsPage({
                    trainerId: this.trainerId,
                    page: targetPage,
                    size: this.pageSize,
                    sort: this.sort,
                    direction: this.direction,
                    products: this.products,
                    search: this.search.trim() || undefined,
                    from: this.from,
                    to: this.to,
                    archived: this.archived,
                });
                this.items = response.projects;
                this.totalRecords = response.totalElements;
                this.page = response.page;
                this.pageSize = response.size;
                this.hasArchivedProjects = response.hasArchived ?? false;
            } finally {
                this.loading = false;
            }
        },

        /** Sets the trainer and loads the first page. Resets filters when the trainer changes. */
        async initialize(trainerId: string, archived = false) {
            if (this.trainerId !== trainerId) {
                this.resetFilters();
            }
            this.trainerId = trainerId;
            this.archived = archived;
            await this.loadPage(0);
        },

        resetFilters() {
            this.search = "";
            this.products = [];
            this.from = undefined;
            this.to = undefined;
            this.page = 0;
        },

        /** Reloads the current page (e.g. after a create/update/delete). */
        async reload() {
            await this.loadPage(this.page);
        },

        async setSort(sort: ProjectSortMode) {
            this.sort = sort;
            await this.loadPage(0);
        },

        async setDirection(direction: ProjectSortDirection) {
            this.direction = direction;
            await this.loadPage(0);
        },

        async setSearch(search: string) {
            this.search = search;
            await this.loadPage(0);
        },

        async setProducts(products: string[]) {
            this.products = products;
            await this.loadPage(0);
        },

        async setDateRange(from: string | undefined, to: string | undefined) {
            this.from = from;
            this.to = to;
            await this.loadPage(0);
        },

        async setArchived(archived: boolean) {
            this.archived = archived;
            await this.loadPage(0);
        },

        /** Returns the full project, loading and caching it on first access. */
        async getDetail(projectId: number): Promise<Project> {
            const cached = this.detailCache[projectId];
            if (cached) return cached;
            const project = await ProfileAdministrationAPI.getProject(this.trainerId, projectId);
            this.detailCache[projectId] = project;
            return project;
        },

        /** Stores a known-full project in the detail cache (e.g. right after creation/update). */
        cacheDetail(project: Project) {
            if (project.id !== undefined) {
                this.detailCache[project.id] = project;
            }
        },

        invalidateDetail(projectId: number) {
            delete this.detailCache[projectId];
        },
    },
});
